"use client";

import { usePathname } from "next/navigation";

import type { LanguageCode } from "@/config/i18n";
import type { Movie, SortOption } from "@/types/movie";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";

import { MovieCard, MovieCardSkeleton } from "./MovieCard";

interface MovieGridProps {
  movies: Movie[];
  isLoading?: boolean;
  sortBy?: SortOption;
  /** Defaults to the language in the current URL. */
  lang?: LanguageCode;
}

/** Two columns on phones, up to four on wide screens. */
const GRID_CLASSES =
  "grid w-full grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 lg:gap-6";

export function MovieGrid({
  movies,
  isLoading = false,
  sortBy,
  lang,
}: MovieGridProps) {
  const pathname = usePathname();
  const t = createTranslator(lang ?? getLanguageFromPathname(pathname));

  if (isLoading) {
    return (
      <div aria-busy="true" className={GRID_CLASSES}>
        {Array.from({ length: 8 }).map((_, i) => (
          <MovieCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (movies.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        role="status"
      >
        <div aria-hidden="true" className="mb-4 text-6xl">
          🎬
        </div>
        <h3 className="mb-2 text-xl font-semibold">{t("movies.noResults")}</h3>
        <p className="text-default-500">{t("movies.noResultsHint")}</p>
      </div>
    );
  }

  return (
    <div className={GRID_CLASSES}>
      {movies.map((movie) => (
        <MovieCard key={movie.id} movie={movie} sortBy={sortBy} />
      ))}
    </div>
  );
}
